// src/pages/admin/ViewEnrollments.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Download,
  Eye,
  FileText,
  Plus,
  RefreshCw,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  FileCheck2,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Input } from "src/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/Table";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { manualEnrollmentService, ManualEnrollmentRecord } from "src/services/manualEnrollmentService";
import { useToast } from "src/hooks/useToast";
import { ROUTES } from "src/routes/RouteConfig";
import LoadingSpinner from "src/components/LoadingSpinner";

const ViewEnrollments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentId = searchParams.get("studentId");
  const initialStatus = searchParams.get("status") || "all";

  const { toast } = useToast();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  const [records, setRecords] = useState<ManualEnrollmentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [state, setState] = useState(initialStatus);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await manualEnrollmentService.list();
      setRecords(data);
    } catch (error) {
      toast({
        title: "Could not load enrollments",
        description: error instanceof Error ? error.message : "Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTitle("View Enrollments");
    setDescription("Browse, search, and manage verified student enrollment records and invoices");
    setActions(null);
    void load();
    return () => reset();
  }, [reset, setActions, setDescription, setTitle]);

  // Keep state filter synced if url status parameter changes
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== state) {
      setState(statusParam);
    }
  }, [searchParams, state]);

  const handleStateChange = (value: string) => {
    setState(value);
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete("status");
    } else {
      newParams.set("status", value);
    }
    setSearchParams(newParams, { replace: true });
  };

  const downloadInvoice = async (record: ManualEnrollmentRecord) => {
    if (!record.invoice_pdf_url) return;
    try {
      const url = await manualEnrollmentService.createInvoiceDownloadUrl(record.invoice_pdf_url);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast({
        title: "Invoice unavailable",
        description: error instanceof Error ? error.message : "Unable to create download link.",
        type: "error",
      });
    }
  };

  const filtered = useMemo(() => {
    return records.filter((record) => {
      const searchable = `${record.student_name} ${record.student_email} ${record.product_name}`.toLowerCase();
      const matchesQuery = searchable.includes(query.toLowerCase());
      const matchesState = state === "all" || record.enrollment_state === state;
      const matchesStudent = !studentId || record.student_id === studentId;
      return matchesQuery && matchesState && matchesStudent;
    });
  }, [records, query, state, studentId]);

  const renderStatusBadge = (enrollmentState: string) => {
    switch (enrollmentState) {
      case "enrolled":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Enrolled
          </span>
        );
      case "awaiting_invoice_upload":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            Awaiting Invoice
          </span>
        );
      case "ready_to_finalize":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <FileCheck2 className="w-3.5 h-3.5 text-blue-600" />
            Ready to Finalize
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            <XCircle className="w-3.5 h-3.5 text-slate-500" />
            Cancelled
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 capitalize">
            {enrollmentState.replace(/_/g, " ")}
          </span>
        );
    }
  };

  const renderBatchBadge = (record: ManualEnrollmentRecord) => {
    if (record.has_batch) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
          Created
        </span>
      );
    }
    if (record.enrollment_state === "enrolled") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
          Eligible
        </span>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation & Top Action */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="hover:text-purple-700 transition"
          >
            Admin
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_ENROLLMENTS.path)}
            className="hover:text-purple-700 transition font-medium"
          >
            Enrollments
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">View Enrollments</span>
        </div>

        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={isLoading}
            className="h-9 px-3 text-xs font-semibold text-slate-700 border-slate-300 hover:bg-slate-50"
          >
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>

          <Button
            size="sm"
            onClick={() => navigate(ROUTES.ADMIN_ENROLLMENTS_CREATE.path)}
            className="h-9 px-3.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="h-3.5 w-3.5" />
            Manual Enrollment
          </Button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <Card className="border border-slate-200/80 shadow-xs">
        <CardContent className="flex flex-col gap-3 pt-6 md:flex-row items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              className="pl-9 h-10 border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by student name, email, or enrolled product..."
            />
          </div>
          <Select value={state} onValueChange={handleStateChange}>
            <SelectTrigger className="w-full md:w-60 h-10 border-slate-300">
              <SelectValue placeholder="All states" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              <SelectItem value="awaiting_invoice_upload">Awaiting Invoice</SelectItem>
              <SelectItem value="ready_to_finalize">Ready to Finalize</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Records Table Card */}
      <Card className="border border-slate-200/80 shadow-xs">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-bold text-slate-900">
                {studentId ? "Student Subscriptions" : "Enrollment Records"}
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-1">
                {studentId
                  ? "Showing enrollments and active subscriptions for the selected student."
                  : "Verified offline enrollments and curriculum subscriptions."}
              </CardDescription>
            </div>
            <div className="text-xs font-semibold text-slate-600 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200 self-start sm:self-auto">
              {filtered.length} {filtered.length === 1 ? "record" : "records"} found
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/75">
              <TableRow>
                <TableHead className="font-semibold text-slate-600 text-xs py-3">Student</TableHead>
                <TableHead className="font-semibold text-slate-600 text-xs py-3">Product</TableHead>
                <TableHead className="font-semibold text-slate-600 text-xs py-3">Amount</TableHead>
                <TableHead className="font-semibold text-slate-600 text-xs py-3">Status</TableHead>
                <TableHead className="font-semibold text-slate-600 text-xs py-3">Batch</TableHead>
                <TableHead className="font-semibold text-slate-600 text-xs py-3 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <LoadingSpinner />
                      <p className="mt-3 text-xs font-medium text-slate-500">Loading enrollment records...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16 text-center">
                    <div className="max-w-md mx-auto space-y-3">
                      <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                        <FileText className="w-6 h-6" />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-800">No enrollments found</h4>
                      <p className="text-xs text-slate-500">
                        {query || state !== "all" || studentId
                          ? "No records match your active search filters. Try clearing or relaxing the search query."
                          : "No manual enrollment workflows have been recorded yet."}
                      </p>
                      {(query || state !== "all") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setQuery("");
                            handleStateChange("all");
                          }}
                          className="text-xs mt-2"
                        >
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((record) => (
                  <TableRow key={record.workflow_id} className="hover:bg-slate-50/50 transition">
                    <TableCell className="py-3.5">
                      <div className="font-semibold text-slate-900 text-sm">{record.student_name}</div>
                      <div className="text-xs text-slate-500">{record.student_email}</div>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <span className="text-sm font-medium text-slate-800">{record.product_name}</span>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <span className="text-sm font-semibold text-slate-900 font-mono">
                        {record.currency.toUpperCase()} {Number(record.amount).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="py-3.5">{renderStatusBadge(record.enrollment_state)}</TableCell>
                    <TableCell className="py-3.5">{renderBatchBadge(record)}</TableCell>
                    <TableCell className="py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          className="h-8 px-2.5 text-xs font-semibold text-slate-700 border-slate-300 hover:bg-slate-50"
                        >
                          <Link to={`${ROUTES.ADMIN_ENROLLMENTS.path}/${record.workflow_id}`}>
                            <Eye className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                            Details
                          </Link>
                        </Button>
                        {record.invoice_pdf_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void downloadInvoice(record)}
                            title="Download Invoice PDF"
                            className="h-8 px-2 text-xs font-semibold text-slate-700 border-slate-300 hover:bg-slate-50"
                          >
                            <Download className="h-3.5 w-3.5 text-purple-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewEnrollments;

